import {
  Component,
  OnInit,
  OnDestroy,
  Input,
  Output,
  EventEmitter
} from '@angular/core';
import { FormArray, FormBuilder, FormGroup, Validators } from '@angular/forms';
import {
  FormFieldType,
  ICrmSchema,
  ICrmSchemaGroup,
  IFormBuilderChangeEvent,
  IFormField,
  ILayout
} from '@appgen-health-crm/api-interfaces';
import { FiledTypes } from '@appgen-health-crm/data';
import { Subject } from 'rxjs';
import { debounceTime, takeWhile } from 'rxjs/operators';
import { cloneDeep, isEqual } from 'lodash';
import { moveItemInArray } from '@angular/cdk/drag-drop';
import { NzMarks } from 'ng-zorro-antd/slider';

interface IActiveField {
  field: IFormField;
  index: number;
  mode: 'edit' | 'add';
}
@Component({
  // eslint-disable-next-line @angular-eslint/component-selector
  selector: 'form-builder',
  templateUrl: './form-builder.component.html',
  styleUrls: ['./form-builder.component.less']
})
export class FormBuilderComponent implements OnInit, OnDestroy {
  dragging: boolean;
  constructor(private fb: FormBuilder) {}

  // this component will be controlled from its parent, by changing the input,
  // and will emit its value on change to be handled on parent component

  edit = true;
  private _fields: IFormField[];
  @Input()
  set fields(val: IFormField[]) {
    // for performance only render when fields values changes
    if (!isEqual(val, this.fields)) {
      this._fields = val;
    }
  }
  get fields(): IFormField[] {
    return this._fields;
  }

  /**
   * predefined typed from layout, will be added to the field type dropdown
   */
  private _schemas: ILayout[];
  // we use setter because this might be undefined on onInit and passed later from an effect
  @Input()
  set schemas(val: ILayout[]) {
    this._schemas = val;
  }
  get schemas(): ILayout[] {
    return this._schemas;
  }

  marks: NzMarks = {
    0: {
      style: {
        color: '#38b7ab'
      },
      label: 'auto'
    },
    25: '25%',
    33: '33%',
    50: '50%',
    75: '75%',
    100: '100%'
  };

  // eslint-disable-next-line @angular-eslint/no-output-on-prefix
  @Output() onChange = new EventEmitter<IFormBuilderChangeEvent>();

  changes$ = new Subject<IFormField[]>();

  _activeField: IActiveField | null = null;

  public set activeField(v: IActiveField | null) {
    if (v) {
      this.activeFieldFG?.patchValue(v.field);
      this.patchConditions(v.field);
      // if (v.mode === 'edit') {
      // this.activeFieldFG?.get('name').disable();
      // }
    } else {
      this.activeFieldFG?.enable();
      this.activeFieldFG?.reset();
    }
    this._activeField = v;
  }

  public get activeField(): IActiveField | null {
    return this._activeField;
  }

  public get conditionsFA(): FormArray {
    return this.activeFieldFG.get('conditions') as FormArray;
  }

  public get fieldType(): string {
    return this.activeFieldFG.get('type').value;
  }

  addFieldIndex: number;

  activeFieldFG: FormGroup;
  active = true;
  filedTypes = FiledTypes;
  filedTypesArr: {
    type: string;
    label: string;
    order: number;
    icon: string;
    disabled?: boolean;
    variants?: string[];
  }[] = [];
  ngOnInit(): void {
    for (const key in this.filedTypes) {
      this.filedTypesArr.push({ ...this.filedTypes[key], type: key });
    }
    this.filedTypesArr.sort((a, b) => a.order - b.order);

    this.activeFieldFG = this.fb.group({
      name: [null, [Validators.required]],
      label: [null, [Validators.required]],
      standaloneLabel: [false, []],
      placeholder: [null, []],
      type: [null, [Validators.required]],
      options: [[], []],
      required: [null, []],
      isHidden: [null, []],
      variant: [null, []],
      defaultValue: [null, []],
      conditions: this.fb.array([]),
      group: [null, []],
      repeatFieldName: [null, []],
      repeatConstant: [null, []],
      repeatType: [null, []],
      width: [null, []],
      publicKey: [null, []]
    });

    this.changes$
      .pipe(
        debounceTime(500),
        takeWhile(() => this.active)
      )
      .subscribe(fields => {
        this.onChange.emit({
          fields: cloneDeep(fields),
          valid: this.validateFields(fields)
        });
      });
  }

  ngOnDestroy() {
    this.active = false;
  }

  addNewField(fieldType: FormFieldType, index: number = this.addFieldIndex) {
    const field = {
      label: '',
      name: '',
      type: fieldType,
      required: false
    };
    this.activeField = {
      field,
      index,
      mode: 'add'
    };
  }

  addCondition(cond?: { name: string; value: 'Yes' | 'No' }) {
    const conditionFG = this.fb.group({
      name: [cond?.name, Validators.required],
      value: [cond?.value, Validators.required]
    });

    this.conditionsFA.push(conditionFG);
  }

  patchConditions(field: IFormField) {
    if (!field?.conditions?.length) return;
    field.conditions.forEach(cond => {
      this.addCondition(cond);
    });
  }

  deleteCondition(index: number) {
    this.conditionsFA.removeAt(index);
  }

  addSchemaField(
    field: IFormField,
    group: ICrmSchemaGroup,
    schema: ICrmSchema,
    index: number = this.addFieldIndex
  ) {
    if (this.checkIfFieldExists(field.name)) {
      alert(`Field '${field.name}' already exists`);
      return;
    }

    const _field = {
      ...field,
      schema: {
        groupId: group._id,
        groupName: group.name,
        isGroupCoreSchemaField: group.isCoreSchemaField,
        groupTitle: group.name,
        schemaName: schema.name,
        schemaTitle: schema.name
      }
    };

    if (index === null || index === undefined) {
      this._fields = [...this._fields, _field];
    } else {
      const fields = [...this.fields];
      fields.splice(index, 0, _field);
      this._fields = fields;
    }

    this.triggerChanges();
  }

  checkIfFieldExists(fieldName: string): boolean {
    return this._fields.some(x => x.name === fieldName);
  }

  removeField(i: number = this.activeField.index) {
    if (i === null || i === undefined) return;
    if (this._fields[i]) {
      const fields = cloneDeep(this._fields);
      const field = fields[i];
      fields.splice(i, 1);
      if (field.type === 'group') {
        fields.forEach(x => {
          if (x.group == field.name) x.group = null;
        });
      }
      this._fields = fields;
    }
    this.triggerChanges();
    this.closeActiveField();
  }

  drop({ previousIndex, currentIndex }) {
    const _fields = cloneDeep(this._fields);
    moveItemInArray(_fields, previousIndex, currentIndex);
    this._fields = _fields;
    this.triggerChanges();
  }

  triggerChanges() {
    this.sortFields();
    this.changes$.next(this.fields);
  }

  setDefaultValue() {
    if (!this.activeField) return;
    if (
      typeof this.activeField?.field?.defaultValue === 'string' &&
      !this.activeField.field?.defaultValue?.trim()
    ) {
      this.activeField.field.defaultValue = null;
      return;
    }
    switch (this.activeField.field.type) {
      case 'date':
        this.activeField.field.defaultValue = new Date(
          this.activeField.field.defaultValue
        );
        break;
      default:
        break;
    }
    this.triggerChanges();
  }

  sortFields() {
    const _fields = cloneDeep(this._fields);
    const map: Record<string, IFormField[]> = {};

    _fields.forEach(field => {
      if (field.type === 'group') {
        map[field.name] = [field, ...(map[field.name] || [])];
        return;
      }

      if (field.group) {
        map[field.group] = [...(map[field.group] || []), field];
        return;
      }
    });

    const sortedFields: IFormField[] = [];
    _fields.forEach(field => {
      if (field.type === 'group') {
        sortedFields.push(...map[field.name]);
        return;
      }

      if (field.group) {
        // already included with its group
        return;
      }

      sortedFields.push(field);
    });

    this._fields = sortedFields;
  }

  validateFields(fields: IFormField[]): boolean {
    for (let i = 0; i < fields.length; i++) {
      const field = fields[i];
      if (!field.label.trim() || !field.name.trim() || !field.type.trim()) {
        return false;
      }
    }
    return true;
  }

  closeActiveField() {
    this.activeField = null;
    this.conditionsFA.clear();
  }

  saveActiveField() {
    for (const i in this.activeFieldFG.controls) {
      this.activeFieldFG.controls[i].markAsDirty();
      this.activeFieldFG.controls[i].updateValueAndValidity();
    }

    if (this.activeFieldFG.invalid) {
      console.error('invalid FG', this.activeFieldFG.errors);
      return;
    }

    const field: IFormField = {
      ...this.activeField.field,
      ...this.activeFieldFG.value
    };

    if (this.activeField.mode === 'add') {
      // add field
      if (this.checkIfFieldExists(this.activeField.field.name)) {
        alert(`Field '${this.activeField.field.name}' already exists`);
        return;
      }

      if (
        this.activeField.index === null ||
        this.activeField.index === undefined
      ) {
        this._fields = [...this._fields, field];
      } else {
        const fields = [...this.fields];
        fields.splice(this.activeField.index, 0, field);
        this._fields = fields;
      }
    } else {
      // edit field
      const fields = [...this.fields];
      fields[this.activeField.index] = field;
      this._fields = fields;
    }

    this.triggerChanges();
    this.closeActiveField();
  }

  openFieldDetails(field: IFormField, index: number) {
    this.activeField = {
      field,
      index,
      mode: 'edit'
    };
  }

  startDraging() {
    this.dragging = true;
  }
  endDraging() {
    this.dragging = false;
  }
}
